import { buildMetaFunnelEvent } from "@circle-react/components/Paywalls/Checkout/helpers/metaFunnelEventHelpers";
import { useCallback, useRef, type MutableRefObject } from "react";

import {
  ConversionTrackingEvent,
  conversionTrackingApi,
  type TrackConversionEventParams,
} from "@/react/api/conversionTrackingApi";
import type { CheckoutAttemptStatus } from "@/react/types/PaywallCheckoutAttempt";

import {
  shouldTrackMetaCompletedAttempt,
  trackMetaEvent,
} from "../../helpers/metaPixel";
import { useMetaPixel } from "./useMetaPixel";

export interface TrackCompletedAttemptMoneyEvent {
  attemptId: number;
  attemptStatus: CheckoutAttemptStatus;
  currency: string;
  eventName: "purchase" | "subscribe" | "start_trial";
  value: number;
}

type FunnelConversionEvent = {
  [EventName in TrackConversionEventParams["eventName"]]: Omit<
    TrackConversionEventParams,
    "eventName"
  > & { eventName: EventName };
}[TrackConversionEventParams["eventName"]];

export type TrackConversionEventInput =
  | FunnelConversionEvent
  | TrackCompletedAttemptMoneyEvent;

export type TrackConversionEvent = (params: TrackConversionEventInput) => void;

type EventName = TrackConversionEventInput["eventName"];
type EventHandlers = {
  [Name in EventName]: (
    params: Extract<TrackConversionEventInput, { eventName: Name }>,
  ) => void;
};

const isProcessableFunnelEvent = (
  params: TrackConversionEventInput,
): params is TrackConversionEventParams =>
  Object.values(ConversionTrackingEvent).some(
    value => value === params.eventName,
  );

const isCompletedAttemptMoneyEvent = (
  params: TrackConversionEventInput,
): params is TrackCompletedAttemptMoneyEvent =>
  params.eventName === "purchase" ||
  params.eventName === "subscribe" ||
  params.eventName === "start_trial";

const fireKey = ({
  funnelUuid,
  eventName,
  paymentType,
}: TrackConversionEventParams) =>
  [funnelUuid, eventName, paymentType].filter(Boolean).join(":");

// PAY-404 (Slice 1.2) -- fire-and-forget POST for the two FE-triggered funnel
// events (`begin_checkout`/`add_payment_info`). Must never throw into or
// block the checkout UI -- mirrors `TriggerEvent`'s server-side never-raise
// contract (Slice 1.2/1.3).
//
// Double-fire guard is a *last-fired* key, not an *ever-fired* set -- see
// `conversionTrackingFiredRef` on `CheckoutSessionContext` for why. Falls
// back to a call-site-local ref only when no shared ref is supplied.
export const useTrackConversionEvent = (
  firedRef?: MutableRefObject<string | null>,
  metaPixelId?: string,
  metaFbc?: string,
) => {
  useMetaPixel(metaPixelId, metaFbc);

  const localFiredRef = useRef<string | null>(null);
  const ref = firedRef ?? localFiredRef;

  const trackConversionEvent = useCallback(
    (params: TrackConversionEventInput) => {
      const funnelEventHandler = (event: FunnelConversionEvent) => {
        if (!isProcessableFunnelEvent(event)) return;

        const key = fireKey(event);
        if (ref.current === key) return;
        ref.current = key;

        conversionTrackingApi.track(event).catch((error: unknown) => {
          if (process.env.NODE_ENV !== "production") {
            console.warn("useTrackConversionEvent failed", error);
          }
        });
        trackMetaEvent(buildMetaFunnelEvent(event, metaPixelId));
      };
      const moneyEventHandler = (event: TrackCompletedAttemptMoneyEvent) => {
        if (!isCompletedAttemptMoneyEvent(event)) return;
        if (
          !event.attemptId ||
          !shouldTrackMetaCompletedAttempt(event.attemptStatus)
        )
          return;

        trackMetaEvent({
          eventName: event.eventName,
          dedupKey: String(event.attemptId),
          pixelId: metaPixelId,
          value: event.value,
          currency: event.currency,
        });
      };
      const eventHandlers: EventHandlers = {
        [ConversionTrackingEvent.BEGIN_CHECKOUT]: funnelEventHandler,
        [ConversionTrackingEvent.ADD_PAYMENT_INFO]: funnelEventHandler,
        purchase: moneyEventHandler,
        subscribe: moneyEventHandler,
        start_trial: moneyEventHandler,
      };

      const dispatch = <Name extends EventName>(
        eventName: Name,
        event: Extract<TrackConversionEventInput, { eventName: Name }>,
      ) => {
        if (!Object.prototype.hasOwnProperty.call(eventHandlers, eventName)) {
          return;
        }

        eventHandlers[eventName](event);
      };

      dispatch(params.eventName, params);
    },
    [ref, metaPixelId],
  );

  return { trackConversionEvent };
};
